import React from 'react';
import { describeEnvironment } from '../lib/pipeline';

type Props = {
  /** Shown in the fallback so it's clear *which* part failed. */
  label: string;
  /**
   * When this value changes the boundary clears itself. Pass whatever the
   * user can change to fix the problem — editing the offending caption should
   * bring the preview back without reloading and losing the transcript.
   */
  resetKey?: unknown;
  children: React.ReactNode;
};

type State = { error: Error | null };

/**
 * Deliberately scoped rather than wrapped around the whole app. The transcript
 * is minutes of GPU time and cannot be recreated from anything on the page, so
 * a throw inside the preview must not take the transcript, the controls or the
 * export button down with it.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[undertone] ${this.props.label} crashed`, error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="boundary" role="alert">
        <strong>{this.props.label} stopped rendering</strong>
        <span>{error.message}</span>
        <span className="mono diag">{describeEnvironment()}</span>
        <p className="help">
          Your transcript is safe — it lives outside this panel. Editing the caption that
          caused it will bring this back, or retry below.
        </p>
        <button className="ghost" onClick={() => this.setState({ error: null })}>
          Retry
        </button>
      </div>
    );
  }
}
